import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

export const listClientsSkill: Skill = {
  id: "listClients",
  description:
    "Lista clientes registrados del restaurante con búsqueda opcional por nombre o teléfono.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      search: { type: "string", description: "Texto a buscar (opcional)" },
    },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Buscando clientes…",
      successMsg: "✓ Clientes obtenidos",
      run: async () => {
        try {
          const params = new URLSearchParams();
          if (args.search) params.set("search", String(args.search));
          params.set("limit", "30");
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/clients?${params.toString()}`,
            ctx.accessToken,
          );
          const items: any[] = data?.items ?? data?.data ?? data ?? [];
          return {
            ok: true as const,
            result: {
              count: items.length,
              clients: items.map((c) => ({
                id: c.id,
                name: c.name,
                phone: c.phone,
                email: c.email,
              })),
            },
            humanSummary: `${items.length} cliente(s).`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const updateClientSkill: Skill = {
  id: "updateClient",
  description:
    "Actualiza los datos de un cliente existente (nombre, teléfono, email, notas).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      clientId: { type: "string" },
      name: { type: "string" },
      phone: { type: "string" },
      email: { type: "string" },
      notes: { type: "string" },
      internalNotes: { type: "string" },
    },
    required: ["restaurantId", "clientId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const clientId = String(args.clientId ?? "");
    if (!restaurantId || !clientId) return { ok: false, error: "Faltan IDs." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (args.name) { patch.name = String(args.name); details.push({ label: "Nombre", value: patch.name as string }); }
    if (args.phone !== undefined) { patch.phone = String(args.phone); details.push({ label: "Teléfono", value: patch.phone as string }); }
    if (args.email !== undefined) { patch.email = String(args.email); details.push({ label: "Email", value: patch.email as string }); }
    if (args.notes !== undefined) patch.notes = String(args.notes);
    if (args.internalNotes !== undefined) patch.internalNotes = String(args.internalNotes);
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({ title: "Actualizar cliente", details, confirmLabel: "Actualizar" });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando cliente…",
      successMsg: "✓ Cliente actualizado",
      restaurantId,
      targetTab: "clientes",
      targetTabLabel: "Clientes",
      run: async () => {
        try {
          const { data } = await apiWithAuth.patch(
            `/restaurants/${restaurantId}/clients/${clientId}`,
            patch,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "clients", action: "updated", restaurantId, id: clientId });
          return { ok: true as const, result: { id: data?.id ?? clientId, ...patch }, humanSummary: "Cliente actualizado." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const deleteClientSkill: Skill = {
  id: "deleteClient",
  description: "Elimina un cliente. IRREVERSIBLE.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, clientId: { type: "string" } },
    required: ["restaurantId", "clientId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const clientId = String(args.clientId ?? "");
    if (!restaurantId || !clientId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Eliminar cliente",
      description: "El cliente se eliminará permanentemente.",
      confirmLabel: "Eliminar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Eliminando cliente…",
      successMsg: "✓ Cliente eliminado",
      restaurantId,
      targetTab: "clientes",
      targetTabLabel: "Clientes",
      run: async () => {
        try {
          await apiWithAuth.delete(
            `/restaurants/${restaurantId}/clients/${clientId}`,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "clients", action: "deleted", restaurantId, id: clientId });
          return { ok: true as const, result: { clientId }, humanSummary: "Cliente eliminado." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
